import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react';
import { cn } from '../cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive-outline' | 'toggle';
/** xs 26 · sm 28 · md 32 (desktop default) · mobile 34 · lg 44 (mobile full-width primary) */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'mobile' | 'lg' | 'responsive';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** toggle variant: current state */
  on?: boolean;
  /** toggle variant: custom fill when on (CSS colour), defaults to --fg / --bg */
  onBg?: string;
  onColor?: string;
  /** Mono micro-label text (ACK, SHOW, CLEAR SCREEN …) */
  mono?: boolean;
  block?: boolean;
}

const sizes: Record<ButtonSize, string> = {
  xs: 'h-[26px] px-2.5 rounded text-xs',
  sm: 'h-7 px-2.5 rounded text-sm',
  md: 'h-8 px-3 rounded-md text-base',
  mobile: 'h-[34px] px-3 rounded-lg text-sm',
  lg: 'h-11 px-4 rounded-xl text-lg w-full',
  responsive: 'h-[34px] px-3 rounded-lg text-md md:h-8 md:rounded-md md:text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', on, onBg, onColor, mono, block, className, style, children, ...rest },
  ref,
) {
  const isOn = variant === 'toggle' && !!on;
  const base = 'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium leading-none select-none disabled:opacity-50 transition-colors';
  const v = cn(
    variant === 'primary' && 'bg-fg text-bg border-0 hover:bg-accent',
    variant === 'secondary' && 'bg-btn text-fg border border-border hover:bg-btn-hover',
    variant === 'ghost' && 'bg-transparent border border-border text-fg3 hover:text-fg',
    variant === 'destructive-outline' && 'bg-transparent border border-[rgba(255,77,61,.4)] text-bad hover:bg-[rgba(255,77,61,.1)]',
    variant === 'toggle' && !isOn && 'bg-btn text-fg border border-border hover:bg-btn-hover',
    variant === 'toggle' && isOn && 'border border-transparent bg-fg text-bg',
  );
  const onStyle: CSSProperties | undefined = isOn && (onBg || onColor) ? { background: onBg ?? 'var(--fg)', color: onColor ?? 'var(--bg)' } : undefined;
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      className={cn(base, sizes[size], v, mono && 'font-mono tracking-meta', block && 'w-full', className)}
      style={{ ...onStyle, ...style }}
      aria-pressed={variant === 'toggle' ? !!on : undefined}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Unstyled text button (OUT ↗, ALL ALERTS →, PUSH UPDATE →). */
export function TextButton({ className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className={cn('border-0 bg-transparent p-0 font-mono text-xs font-medium tracking-meta text-fg3 hover:text-fg', className)} {...rest} />;
}
